import { useCallback } from 'react';
import { useResponse } from './useResponse';
import { API } from '../../common/config';
import { convertStringCamelCaseToSnakeCase } from '../../utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import convertSnakeCaseToCamelCase from '../../utilities/convertSnakeCaseToCamelCase/convertSnakeCaseToCamelCase';
import BaseServiceRetryWrapper from '../baseRetry';
import { SheetAttributeMapping } from '../sheet-attribute-manager/models/sheet-attribute-manager.interface';

const base = `${API.BASE_DEV_HOST}/api/attribute-mappings`;
const ATTRIBUTE_MAPPER = `${base}/attribute-mapping`;

export enum AttributeType {
  FINANCIALS = 'financials',
  CAP_TABLE = 'cap_table',
}

class AttributeMapperService extends BaseServiceRetryWrapper {
  retrieve = (id: number, attributeType: AttributeType): Promise<SheetAttributeMapping> => this.get(`${base}/${id}?attribute-type=${attributeType}`) as Promise<SheetAttributeMapping>;
}

export const useRetrieveAttributeMapper = () => {

  const { processErrorResponse } = useResponse();

  const retrieve = useCallback(
    async (id: number, attributeType: AttributeType): Promise<SheetAttributeMapping> => {
      const service = new AttributeMapperService();
      try {
        const response = await service.retrieve(id, attributeType);

        // convert all the keys to camelCase which is what we expect on the client
        const attributeMapper = convertSnakeCaseToCamelCase(response);

        // We must convert the row keys back to snake_case as the values inside the MultiSelectValueViewer are in snake case
        // when the pushing procedure is initiated if the row values are not inline with the values in the desired spreadsheet then
        // the numbers desired to push into the spreadsheet will now know where to push to
         
        Object.keys(attributeMapper.data).forEach(
          (columnKey) => {
            Object.keys(attributeMapper.data[columnKey]).forEach(
              rowKey => {
                const convertedRowKey = convertStringCamelCaseToSnakeCase(rowKey);
                attributeMapper.data[columnKey][convertedRowKey] = attributeMapper.data[columnKey][rowKey];
                delete attributeMapper.data[columnKey][rowKey];
              }
            )
          }
        )

        return attributeMapper;
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while loading the attribute mapper';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'get the financial statement',
        });
        throw error;
      }
    },
    [processErrorResponse]
  );

  return [retrieve];
};

export const useUpdateAttributeMapping = () => {
  const { processErrorResponse } = useResponse();

  const updateMapping = useCallback(
    async (mappingId: number, updates: any): Promise<SheetAttributeMapping> => {
      const service = new AttributeMapperService();
      try {
        const response = await service.patch(`${base}/${mappingId}`, updates);
        return convertSnakeCaseToCamelCase(response);
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while updating the attribute mapping';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'update the attribute mapping',
        });
        throw error;
      }
    },
    [processErrorResponse]
  );

  return [updateMapping];
};

export const useAcceptAllAIMappings = () => {
  const { processErrorResponse } = useResponse();

  const acceptAll = useCallback(
    async (mappingId: number): Promise<SheetAttributeMapping> => {
      const service = new AttributeMapperService();
      try {
        const response = await service.patch(`${base}/${mappingId}/accept-all`, {});

        // convert all the keys to camelCase which is what we expect on the client
        const attributeMapper = convertSnakeCaseToCamelCase(response);

        // Convert row keys back to snake_case
        Object.keys(attributeMapper.data).forEach(
          (columnKey) => {
            Object.keys(attributeMapper.data[columnKey]).forEach(
              rowKey => {
                const convertedRowKey = convertStringCamelCaseToSnakeCase(rowKey);
                attributeMapper.data[columnKey][convertedRowKey] = attributeMapper.data[columnKey][rowKey];
                delete attributeMapper.data[columnKey][rowKey];
              }
            )
          }
        )

        return attributeMapper;
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while accepting all AI mappings';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'accept all AI mappings',
        });
        throw error;
      }
    },
    [processErrorResponse]
  );

  return [acceptAll];
};

export const useDeclineAllAIMappings = () => {
  const { processErrorResponse } = useResponse();

  const declineAll = useCallback(
    async (mappingId: number): Promise<SheetAttributeMapping> => {
      const service = new AttributeMapperService();
      try {
        const response = await service.patch(`${base}/${mappingId}/decline-all`, {});

        // convert all the keys to camelCase which is what we expect on the client
        const attributeMapper = convertSnakeCaseToCamelCase(response);

        // Convert row keys back to snake_case
        Object.keys(attributeMapper.data).forEach(
          (columnKey) => {
            Object.keys(attributeMapper.data[columnKey]).forEach(
              rowKey => {
                const convertedRowKey = convertStringCamelCaseToSnakeCase(rowKey);
                attributeMapper.data[columnKey][convertedRowKey] = attributeMapper.data[columnKey][rowKey];
                delete attributeMapper.data[columnKey][rowKey];
              }
            )
          }
        )

        return attributeMapper;
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while declining all AI mappings';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'decline all AI mappings',
        });
        throw error;
      }
    },
    [processErrorResponse]
  );

  return [declineAll];
};